import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useIntl } from "react-intl";
import ISerial from "@models/ISerial";
import ICatalog_Item from "@models/ICatalog_Item";
import IOnReclassifySerialDTO from "@models/DTO/IOnReclassifySerialDTO";
import { useAppContextNonNull } from "@contexts/AppContext";
import SerialService from "@services/SerialService";
import OwButton from "@components/shared/OwButton/OwButton";
import { FinishedSkuPicker } from "../FinishedSkuPicker/FinishedSkuPicker";
import "./ReclassifySerialModal.scss";

type Props = {
    /** The serial to classify; the modal is shown while it is set. */
    serial: ISerial | null;
    closeFn: () => void;
    onReclassified: (result: IOnReclassifySerialDTO) => void;
};

/**
 * "Set finished product" (OR-1459): pick a serialized SKU, confirm "Classify serial X as SKU?",
 * then re-SKU the serial. The serial number never changes, nor does its workflow step.
 */
export const ReclassifySerialModal = (props: Props) => {
    const { serial, closeFn, onReclassified } = props;
    const { userModel, supplierModel, vendorModel } = useAppContextNonNull();
    const intl = useIntl();

    const [selectedItem, setSelectedItem] = useState<ICatalog_Item | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    // Bumped whenever the modal's serial changes (including close): a save still in flight for the
    // previous serial must not report into, close, or unlock the modal now showing another one.
    const sessionId = useRef(0);

    useEffect(() => {
        sessionId.current++;
        setSelectedItem(null);
        setErrorMessage("");
        setIsSaving(false);
    }, [serial?.id]);

    const save = async () => {
        if (!serial || !selectedItem || isSaving) return;

        const saveSessionId = sessionId.current;
        const isCurrent = () => saveSessionId === sessionId.current;

        try {
            setIsSaving(true);
            setErrorMessage("");
            const result = await SerialService.reclassifySerial(
                serial.id,
                { catalogItemGuid: selectedItem.guid },
                userModel,
                supplierModel,
                vendorModel
            );

            if (isCurrent()) {
                onReclassified(result);
            }
        } catch (error: any) {
            if (isCurrent()) {
                setErrorMessage(
                    error?.message ||
                        intl.formatMessage({ id: "reclassify_serial_failed", defaultMessage: "Could not set the finished product" })
                );
            }
        } finally {
            if (isCurrent()) {
                setIsSaving(false);
            }
        }
    };

    return (
        <Modal show={!!serial} onHide={closeFn} centered className="ReclassifySerialModal">
            <Modal.Header closeButton>
                <Modal.Title>{intl.formatMessage({ id: "set_finished_product", defaultMessage: "Set finished product" })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {serial && (
                    <>
                        <div className="mb-2">
                            <span className="text-muted">
                                {intl.formatMessage({ id: "current_finished_product", defaultMessage: "Current finished product" })}:
                            </span>{" "}
                            <strong>{serial.Catalog_Item?.sku || "-"}</strong>
                        </div>
                        <FinishedSkuPicker
                            defaultCatalogId={serial.catalog_id}
                            excludeCatalogItemId={serial.catalog_item_id}
                            selectedItem={selectedItem}
                            onSelect={setSelectedItem}
                        />
                        {selectedItem && (
                            <div className="mt-3 fw-bold" data-testid="reclassify-confirm-text">
                                {intl.formatMessage(
                                    { id: "classify_serial_as_sku", defaultMessage: "Classify serial {value} as {sku}?" },
                                    { value: serial.value, sku: selectedItem.sku }
                                )}
                            </div>
                        )}
                        {errorMessage && <div className="text-danger small mt-2">{errorMessage}</div>}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <OwButton
                    title={intl.formatMessage({ id: "cancel", defaultMessage: "Cancel" })}
                    skipTranslate={true}
                    className="btn-danger"
                    fn={closeFn}
                />
                <OwButton
                    title={intl.formatMessage({ id: "confirm", defaultMessage: "Confirm" })}
                    skipTranslate={true}
                    className="btn-success"
                    fn={save}
                    disabled={!selectedItem || isSaving}
                />
            </Modal.Footer>
        </Modal>
    );
};
